import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "isSticky", "connectDragSource", "connectDragPreview", "connectDropTarget", "isDragging", "isStacked", "data-cell-id"],
  _excluded2 = ["onMove", "onDragEnd", "itemId", "index", "type", "component"];
import React, { useEffect } from 'react';
import { View } from '@instructure/ui-view';
// Custom comparison function to prevent re-renders when column order changes
// but individual column data hasn't changed
var arePropsEqual = function arePropsEqual(prevProps, nextProps) {
  // Check stable identifier
  if (prevProps.id !== nextProps.id) return false;
  if (prevProps['data-cell-id'] !== nextProps['data-cell-id']) return false;
  if (prevProps['data-col-key'] !== nextProps['data-col-key']) return false;

  // Check visual props
  if (prevProps.width !== nextProps.width) return false;
  if (prevProps.isSticky !== nextProps.isSticky) return false;
  if (prevProps.isStacked !== nextProps.isStacked) return false;

  // Check keyboard navigation
  if (prevProps.onKeyDown !== nextProps.onKeyDown) return false;
  if (prevProps.tabIndex !== nextProps.tabIndex) return false;

  // Check drag-drop state (these should be stable via useCallback)
  if (prevProps.connectDragSource !== nextProps.connectDragSource) return false;
  if (prevProps.connectDragPreview !== nextProps.connectDragPreview) return false;
  if (prevProps.connectDropTarget !== nextProps.connectDropTarget) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;

  // onMove and onDragEnd should be stable (useCallback in stories/parent)
  // but we don't need to check them since they're filtered out before DOM

  // Compare children by reference - function children must be stabilized with useCallback
  // by the caller if re-renders should be avoided
  if (prevProps.children !== nextProps.children) return false;
  return true;
};
var ColumnHeaderComponent = function ColumnHeaderComponent(_ref) {
  var children = _ref.children,
    isSticky = _ref.isSticky,
    connectDragSource = _ref.connectDragSource,
    connectDragPreview = _ref.connectDragPreview,
    connectDropTarget = _ref.connectDropTarget,
    isDragging = _ref.isDragging,
    isStacked = _ref.isStacked,
    dataCellId = _ref['data-cell-id'],
    viewProps = _objectWithoutProperties(_ref, _excluded);
  // Filter out drag-drop specific props that shouldn't be passed to DOM
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  var _ref2 = viewProps,
    onMove = _ref2.onMove,
    onDragEnd = _ref2.onDragEnd,
    itemId = _ref2.itemId,
    index = _ref2.index,
    type = _ref2.type,
    component = _ref2.component,
    cleanViewProps = _objectWithoutProperties(_ref2, _excluded2);

  // Use an empty image as the drag preview to prevent the browser from
  // screenshotting the table structure when dragging a <th> child
  useEffect(function () {
    if (connectDragPreview) {
      var emptyImage = new Image();
      emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      connectDragPreview(emptyImage, {
        captureDraggingState: true
      });
    }
  }, [connectDragPreview]);
  var headerContentStyle = React.useMemo(function () {
    return {
      opacity: isDragging ? 0.5 : 1,
      transition: 'opacity 0.15s ease-in-out',
      width: cleanViewProps.width,
      cursor: connectDragSource ? 'grab' : 'default'
    };
  }, [isDragging, cleanViewProps.width, connectDragSource]);
  var elementRef = React.useCallback(function (ref) {
    if (connectDropTarget && ref instanceof HTMLElement) {
      connectDropTarget(ref);
    }
  }, [connectDropTarget]);
  var headerContent = /*#__PURE__*/React.createElement("div", {
    style: headerContentStyle,
    "data-testid": "column-header-content"
  }, children);
  var content = /*#__PURE__*/React.createElement(View, Object.assign({
    "data-cell-id": dataCellId,
    as: isStacked ? 'div' : 'th',
    scope: "col",
    focusPosition: "inset",
    elementRef: elementRef,
    position: isSticky ? 'sticky' : 'static',
    insetInlineStart: isSticky ? '0' : void 0,
    background: !cleanViewProps.background ? 'primary' : cleanViewProps.background,
    cursor: connectDragSource ? 'grab' : 'default',
    stacking: "above"
  }, cleanViewProps), connectDragSource ? connectDragSource(headerContent) : headerContent);
  return content;
};
export var ColumnHeader = /*#__PURE__*/React.memo(ColumnHeaderComponent, arePropsEqual);
ColumnHeader.displayName = 'ColumnHeader';